import React, { PropsWithChildren, ReactNode, useRef, useLayoutEffect } from "react";
import { Box } from "@mui/material";
import { Stage } from "@pixi/react";
import { Point } from "pixi.js";
import { Viewport as RawViewport } from "pixi-viewport";
import Viewport, { PlanViewMode } from "../PixiViewport/PixiViewport";
import { IFloorPayload } from "../../Domain/Types/FloorPlan/FloorPayload.type";
import { ICoordinate } from "../../Domain/Types/FloorPlan/Coordinate.type";
import { IViewport } from "../../Domain/Types/FloorPlan/Viewport.type";
import { FloorZoomMode } from "../../Domain/Types/FloorZoomMode.type";

interface Props {
  planViewMode: PlanViewMode;
  fixedWidth?: number;
  fixedHeight?: number;
  floorPlanName: string;
  displayTopToolbar?: boolean;
  initialZoomEnd: Point;
  currentFloorPlan: IFloorPayload | undefined;
  onClickViewport?: (point: Point) => void;
  parent?: React.MutableRefObject<HTMLElement | null>;
  blockViewportMovement?: boolean;
  toolbars?: ReactNode;
  onInit?: (viewport: RawViewport) => void;
  onExtract?: (instance: HTMLImageElement) => void;
  isExtractingCanvas?: boolean;
  setIsExtractingCanvas?: (isExtracting: boolean) => void;
  initialPos?: ICoordinate;
  floorZoomMode?: FloorZoomMode;
  viewportDimensions: IViewport;
  setViewportDimensions: React.Dispatch<React.SetStateAction<IViewport>>;
  setActiveTouchCount?: (count: number) => void;
  floorRotation?: number;
  placeScale: number;
}

export function DisplayViewport({
  planViewMode,
  fixedWidth,
  fixedHeight,
  children,
  initialZoomEnd,
  currentFloorPlan,
  onClickViewport,
  blockViewportMovement,
  toolbars,
  onInit,
  onExtract,
  isExtractingCanvas,
  setIsExtractingCanvas,
  initialPos,
  floorZoomMode,
  viewportDimensions,
  setViewportDimensions,
  setActiveTouchCount,
  floorRotation,
  placeScale
}: PropsWithChildren<Props>) {
  const viewportParentDivRef = useRef<HTMLDivElement>(null);

  const syncActiveTouchCount = (touchesLength: number) => {
    setActiveTouchCount?.(touchesLength);
  };

  const usesActiveTouchLifecycle = planViewMode === PlanViewMode.BOOKING;

  useLayoutEffect(() => {
    if (fixedWidth && fixedHeight) {
      setViewportDimensions({ width: fixedWidth, height: fixedHeight });
      return;
    }

    const parent = viewportParentDivRef.current;
    const applyParentSize = () => {
      const rect = parent?.getBoundingClientRect();
      if (!rect?.width || !rect?.height) {
        return false;
      }

      const width = Math.round(rect.width);
      const height = Math.round(rect.height);
      setViewportDimensions(previous => {
        if (previous.width === width && previous.height === height) {
          return previous;
        }
        return { width, height };
      });
      return true;
    };

    if (!applyParentSize()) {
      setViewportDimensions({ width: 800, height: 600 });
    }

    if (!parent || typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      applyParentSize();
    });
    resizeObserver.observe(parent);

    return () => {
      resizeObserver.disconnect();
    };
  }, [fixedHeight, fixedWidth, setViewportDimensions]);

  return (
    <Box height={"100%"} position={"relative"} data-testid={"viewport-wrapper"} display={"flex"}>
      <Box
        zIndex={0}
        position={"absolute"}
        width={"100%"}
        height={"100%"}
        ref={viewportParentDivRef}
      >
        <Stage
          id="pixi-viewport-stage-element"
          data-testid="pixi-viewport-stage"
          width={viewportDimensions.width}
          height={viewportDimensions.height}
          options={{
            backgroundColor: 0xeeeeee,
            hello: true,
            eventMode: "static",
            clearBeforeRender: true,
            antialias: false
          }}
          onTouchStart={e => syncActiveTouchCount(e.touches.length)}
          onTouchEnd={
            usesActiveTouchLifecycle ? e => syncActiveTouchCount(e.touches.length) : undefined
          }
          onTouchCancel={usesActiveTouchLifecycle ? () => syncActiveTouchCount(0) : undefined}
          onTouchMove={
            usesActiveTouchLifecycle ? e => syncActiveTouchCount(e.touches.length) : undefined
          }
        >
          <Viewport
            blockViewportMovement={blockViewportMovement}
            width={viewportDimensions.width}
            height={viewportDimensions.height}
            initialZoomEnd={initialZoomEnd}
            onClickViewport={onClickViewport}
            onInit={onInit}
            onExtract={onExtract}
            currentFloorPlan={currentFloorPlan}
            initialPos={initialPos}
            floorZoomMode={floorZoomMode}
            isExtractingCanvas={isExtractingCanvas}
            floorRotation={floorRotation}
            setIsExtractingCanvas={setIsExtractingCanvas}
            planViewMode={planViewMode}
            placeScale={placeScale}
          >
            {children}
          </Viewport>
        </Stage>
      </Box>

      {toolbars}
    </Box>
  );
}
